import {Component, Input, input, OnInit, output} from '@angular/core';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  Subject,
  switchMap,
  take,
  takeUntil
} from 'rxjs';
import {Destroyable} from '../../destroyable/destroyable.component';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  FormGroupDirective,
  ValidationErrors,
  Validator
} from '@angular/forms';
import {SubscriptSizing} from '@angular/material/form-field';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {ErrorStateMatcher} from '@angular/material/core';
import {Identifiable, Nameable} from '../../../services/model';
import {toSignal} from '@angular/core/rxjs-interop';
import {filter} from 'rxjs/operators';

@Component({
  template: '',
  standalone: true
})
export abstract class InputAutocompleteComponent<T extends Identifiable & Nameable> extends Destroyable implements ControlValueAccessor, Validator, OnInit {
  parentFormSubmitted = false;
  validatorsUnset = true;

  multiselect = input<boolean>(false);
  firstOptionAsDefault = input<boolean>(false);

  protected valueSubject = new Subject<string | undefined>();
  valueSignal = toSignal(this.valueSubject.asObservable());
  protected $value = this.valueSubject.asObservable().pipe(takeUntil(this.destroy$), distinctUntilChanged(),
    filter(text => !!text));

  @Input() set value(value: string | undefined) {
    this.valueSubject.next(value);
  }

  showEmptyOption = input<boolean>(true);
  valueProperty = input<string>();

  @Input() disabled1 = false;

  selectedOption = output<T | undefined>();

  options: Subject<T[]> = new Subject();
  autocompleteForm: FormGroup;
  subscriptSizing: SubscriptSizing = 'fixed';

  @Input() set dynamicSubscriptSizing(dynamicSubscriptSizing: boolean) {
    this.subscriptSizing = dynamicSubscriptSizing ? 'dynamic' : 'fixed';
  }

  @Input() formInputPlaceholderKey = this.getPlaceholder();

  displayFn: (id: T) => string = this.getDisplayFn();

  readonly errorStateMatcher: ErrorStateMatcher = {
    isErrorState: (ctrl: FormControl) => (ctrl && (ctrl.touched || ctrl.dirty || this.parentFormSubmitted) && ctrl.invalid)
  };

  protected constructor(fb: FormBuilder, formGroupDirective?: FormGroupDirective) {
    super();

    formGroupDirective?.ngSubmit.subscribe(() => this.parentFormSubmitted = true);

    this.autocompleteForm = fb.group({
      input: [this.valueSignal()]
    });

    this.autocompleteForm.get('input')!.valueChanges.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map(value => typeof value === 'string' ? value : value?.name),
      distinctUntilChanged(),
      switchMap(textPattern => {
        return this.getData(textPattern ? textPattern : '');
      })).subscribe(data => this.options.next(data));

  }

  ngOnInit(): void {
    this.$value.subscribe(value => {
        if (value === null) {
          this.reset();
        }
        this.getData('').subscribe(data => {
          this.options.next(data);
          const selectedOption = data.find(option => option.id === value);
          if (selectedOption) {
            this.autocompleteForm.get('input')?.setValue(selectedOption);
            if (data.length > 1) {
              this.options.next([selectedOption]);
            }
            this.writeValue(selectedOption);
          }
        });
      });
    if (this.firstOptionAsDefault() && !this.valueSignal()) {
      this.options.asObservable()
        .pipe(take(1))
        .subscribe(options => {
          const value = options[0];
          this.writeValue(value);
          this.autocompleteForm.get('input')?.setValue(value);
          this.selectedOption.emit(value);
        });
    }
  }

  protected reset(): void {
    this.autocompleteForm.reset();
    this.options.next([]);
    this.valueSubject.next('');
  }

  abstract getData(textPatten: string): Observable<T[]>;

  abstract getPlaceholder(): string;

  abstract getDisplayFn(): (id: T) => string;

  onOptionSelected($event: MatAutocompleteSelectedEvent): void {
    this.selectedOption.emit($event.option.value);
    this.onChange($event.option.value?.id);
    this.onTouch();
  }

  onChange: any = () => {
  };

  onTouch: any = () => {
  };

  writeValue(value: any): void {
    if (!this.valueSignal() && !this.firstOptionAsDefault()) {
      this.getData('').subscribe(data => {
        this.options.next(data);
        const selectedOption = data.find(option => option.id === value);
        if (selectedOption) {
          this.autocompleteForm.get('input')?.setValue(selectedOption);
          if (data.length > 1) {
            this.options.next([selectedOption]);
          }
        }
        const updatedValue = (value && this.valueProperty()) ? value[this.valueProperty() as string] : value;
        this.valueSubject.next(updatedValue);
      });
    } else {
      this.valueSubject.next(value);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled1 = isDisabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const formControl = this.autocompleteForm.get('input');
    if (this.validatorsUnset && control.validator) {
      this.validatorsUnset = false;
      formControl?.setValidators(control.validator);
      formControl?.updateValueAndValidity();
    }

    if (formControl?.touched || this.parentFormSubmitted) {
      return formControl?.invalid ? {invalid: true} : null;
    }
    return null;
  }
}
